import React, { useEffect } from "react";
import { cn } from "../lib/utils";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { useApp } from "../context/AppContext";

export function Toaster() {
  const { toasts, dismissToast } = useApp();
  return (
    <div className="fixed bottom-5 right-5 z-[100] flex flex-col gap-2 w-[340px] max-w-[calc(100vw-2.5rem)]" data-testid="toaster">
      {toasts.map((tt) => (
        <div
          key={tt.id}
          data-testid="toast"
          className="flex items-start gap-3 rounded-xl border bg-surface px-4 py-3 animate-in slide-in-from-right-4"
          style={{ boxShadow: "var(--shadow)" }}
        >
          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#34D399" }} />
          <p className="flex-1 text-sm text-text-primary leading-snug">{tt.message}</p>
          <button onClick={() => dismissToast(tt.id)} className="text-text-secondary hover:text-text-primary transition-colors" data-testid="toast-dismiss">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

export function Card({ className, children, hover = false, ...props }) {
  return (
    <div
      className={cn(
        "rounded-2xl border bg-surface p-6 transition-all duration-200",
        hover && "hover:-translate-y-[2px] cursor-pointer",
        className
      )}
      style={{ boxShadow: "var(--shadow)" }}
      {...props}
    >
      {children}
    </div>
  );
}

export function SectionLabel({ children, className }) {
  return (
    <span
      className={cn(
        "text-[11px] tracking-[0.2em] uppercase font-semibold text-text-secondary",
        className
      )}
    >
      {children}
    </span>
  );
}

const STATUS_STYLES = {
  paid: { bg: "rgba(16,185,129,0.18)", color: "#34D399" },
  done: { bg: "rgba(16,185,129,0.18)", color: "#34D399" },
  active: { bg: "rgba(16,185,129,0.18)", color: "#34D399" },
  pending: { bg: "rgba(245,158,11,0.18)", color: "#FBBF24" },
  open: { bg: "rgba(245,158,11,0.18)", color: "#FBBF24" },
  paused: { bg: "rgba(161,161,170,0.18)", color: "#A1A1AA" },
  late: { bg: "rgba(239,68,68,0.18)", color: "#F87171" },
  refunded: { bg: "rgba(239,68,68,0.18)", color: "#F87171" },
  sent: { bg: "rgba(59,130,246,0.18)", color: "#60A5FA" },
};

export function StatusPill({ status, label, className, ...props }) {
  const s = STATUS_STYLES[status] || { bg: "var(--muted)", color: "var(--text-secondary)" };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium capitalize",
        className
      )}
      style={{ backgroundColor: s.bg, color: s.color }}
      {...props}
    >
      {label || status}
    </span>
  );
}

export function EntityDot({ color, className }) {
  return (
    <span
      className={cn("inline-block w-2 h-2 rounded-full", className)}
      style={{ backgroundColor: color }}
    />
  );
}

export function Button({ variant = "primary", size = "md", className, children, ...props }) {
  const variants = {
    primary: "bg-text-primary text-background hover:opacity-90",
    outline: "border border-[var(--border-strong)] text-text-primary hover:bg-surface-hover",
    ghost: "text-text-secondary hover:bg-surface-hover hover:text-text-primary",
    danger: "bg-red-500/15 text-red-400 hover:bg-red-500/25",
    destructive: "bg-red-500 text-white hover:bg-red-600",
  };
  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-sm",
  };
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-full font-medium transition-all active:scale-[0.97] disabled:opacity-50 disabled:pointer-events-none",
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function Segmented({ options, value, onChange, testid }) {
  return (
    <div className="inline-flex items-center gap-1 rounded-full border bg-surface p-1" data-testid={testid}>
      {options.map((opt) => (
        <button
          key={opt.value}
          data-testid={`${testid}-${opt.value}`}
          onClick={() => onChange(opt.value)}
          className={cn(
            "px-3.5 py-1.5 rounded-full text-xs font-medium transition-all",
            value === opt.value
              ? "bg-text-primary text-background"
              : "text-text-secondary hover:text-text-primary"
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function Spinner({ className }) {
  return <Loader2 className={cn("spin", className)} />;
}

export function Modal({ open, onClose, title, children, footer }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border bg-surface shadow-2xl animate-fade-up max-h-[90vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-heading font-medium">{title}</h3>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
            data-testid="modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 overflow-y-auto">{children}</div>
        {footer && <div className="px-6 py-4 border-t flex justify-end gap-3">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block mb-4">
      <span className="block text-xs font-medium text-text-secondary mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export function Input({ className, ...props }) {
  return (
    <input
      className={cn(
        "w-full rounded-lg border bg-background px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-[var(--border-strong)]",
        className
      )}
      {...props}
    />
  );
}

export function Select({ className, children, ...props }) {
  return (
    <select
      className={cn(
        "w-full rounded-lg border bg-background px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-[var(--border-strong)]",
        className
      )}
      {...props}
    >
      {children}
    </select>
  );
}
